"use strict";

const PERSONALIZATION_EMOJI_REGEX = /[\p{Extended_Pictographic}\p{Regional_Indicator}\uFE0F\u200D]/u;
const PERSONALIZATION_DEDICATION_SIGNATURE_REGEX = /XXX\s*$/;
const PERSONALIZATION_ALL_UPPERCASE_REGEX = /^\P{Ll}+$/u;

function removePersonalizationEmojis(value) {
    return String(value || "").replace(/[\p{Extended_Pictographic}\p{Regional_Indicator}\uFE0F\u200D]/gu, "");
}

function sanitizePersonalizationFieldValue(field) {
    if (!field) return { changed: false, value: "" };

    const currentValue = String(field.value || "");
    const selectionStart = field.selectionStart || 0;
    const sanitizedValue = removePersonalizationEmojis(currentValue);

    if (sanitizedValue === currentValue) {
        return { changed: false, value: sanitizedValue };
    }

    const sanitizedBeforeCursor = removePersonalizationEmojis(currentValue.slice(0, selectionStart));
    field.value = sanitizedValue;

    if (typeof field.setSelectionRange === "function") {
        const nextCursorPosition = sanitizedBeforeCursor.length;
        field.setSelectionRange(nextCursorPosition, nextCursorPosition);
    }

    return { changed: true, value: sanitizedValue };
}

window.PersonalizationFieldSanitizer = {
    removeEmojis: removePersonalizationEmojis,
    sanitizeFieldValue: sanitizePersonalizationFieldValue,
};

/**
 * Personalization Modal Web Component
 * Handles the book personalization form and cart submission
 */
class PersonalizationModal extends HTMLElement {
    constructor() {
        super();
        this.boundHandleAddToCart = this.handleAddToCart.bind(this);
        this.boundHandleStepClick = this.handleStepClick.bind(this);
        this.boundHandleInput = this.handleInput.bind(this);
        this.boundHandleFocus = this.handleFocus.bind(this);
        this.boundHandlePointerDown = this.handlePointerDown.bind(this);
        this.boundHandleNavClick = this.handleNavClick.bind(this);
        this.boundHandleDocClick = this.handleDocClick.bind(this);
        this.boundHandleTriggerOpen = this.handleTriggerOpen.bind(this);
        this.variantId = this.getAttribute("data-variant-id");
        this.shouldOpenModal = this.getAttribute("data-should-open-modal") === "true";
        this.editMode = window.location.search.includes("edit=book") ? "book" : window.location.search.includes("edit=other") ? "other" : null;
        this.lastPointerDownTarget = null;
    }

    connectedCallback() {
        this.addToCartButtons = this.querySelectorAll("[data-add-personalized-to-cart]");
        this.addToCartButtons.forEach((button) => {
            button.addEventListener("click", this.boundHandleAddToCart);
        });

        // Steps and dynamic content
        this.stepHeaders = this.querySelectorAll(".personalization-step");
        this.dynamicTitleEl = this.querySelector("[data-step-dynamic-title]");
        this.dynamicDescriptionEl = this.querySelector("[data-step-dynamic-description]");
        this.productId = this.getAttribute("data-product-id") || this.closest("modal-drawer")?.id?.replace("personalize-modal-", "") || "unknown";

        this.stepHeaders.forEach((step) => step.addEventListener("click", this.boundHandleStepClick));

        this.stepForms = this.querySelectorAll(".personalization-step-form");
        this.navButtons = this.querySelectorAll("[data-next-step], [data-prev-step], [data-goto-step]");
        this.navButtons.forEach((btn) => btn.addEventListener("click", this.boundHandleNavClick));
        document.addEventListener("click", this.boundHandleDocClick);

        const triggerSelector = `modal-trigger[target="#personalize-modal-${this.productId}"]`;
        this.triggerEl = document.querySelector(triggerSelector);
        if (this.triggerEl) {
            this.triggerEl.addEventListener("click", this.boundHandleTriggerOpen, { capture: true });
        }

        const params = new URLSearchParams(window.location.search || "");
        let editState = "";
        if (params.has("edit")) {
            editState = params.get("edit");
        }
        if (editState === "book") {
            this.activateStep(1);
        }
        if (editState === "other") {
            this.activateStep(2);
        }
        if(editState == '') {
            this.activateStep(1);
        }
    }

    disconnectedCallback() {
        this.addToCartButtons.forEach((button) => {
            button.removeEventListener("click", this.boundHandleAddToCart);
        });

        this.stepHeaders?.forEach((step) => step.removeEventListener("click", this.boundHandleStepClick));
        this.navButtons?.forEach((btn) => btn.removeEventListener("click", this.boundHandleNavClick));
        document.removeEventListener("click", this.boundHandleDocClick);
        if (this.triggerEl) {
            this.triggerEl.removeEventListener("click", this.boundHandleTriggerOpen, true);
        }
        if (this.formEl) {
            this.formEl.querySelectorAll("input, textarea, select").forEach((input) => {
                input.removeEventListener("input", this.boundHandleInput);
                input.removeEventListener("focus", this.boundHandleFocus);
                input.removeEventListener("pointerdown", this.boundHandlePointerDown);
                input.removeEventListener("mousedown", this.boundHandlePointerDown);
                input.removeEventListener("touchstart", this.boundHandlePointerDown);
            });
        }
    }

    async handleAddToCart(event) {
        event.preventDefault();
        const cart = await this.getCart();
        const button = event.target;
        const root = this.getModalRoot();
        const forms = root.querySelectorAll(".personalization-step-form");

        if (!forms || forms.length === 0) {
            return;
        }

        // Validate steps in order of user priority; if invalid, navigate to that step
        const stepsToValidate = ["1", "2", "3"];
        for (const sid of stepsToValidate) {
            const { valid, errors } = this.validateStep(sid);
            if (!valid) {
                this.activateStep(sid);
                this.updateErrors(sid, errors);
                return;
            }
        }

        this.setLoadingState(button, true);

        if (!this.variantId) {
            throw new Error("Variant unavailable. Please select a valid variant.");
        }

        const combinedEntries = {};
        forms.forEach((f) => {
            const fd = new FormData(f);
            for (let [key, value] of fd.entries()) {
                combinedEntries[key] = value;
            }
        });

        const editLineUuid = String(this.getSavedState()?.["__line_uuid"] || "");
        const lineItem = cart.items.find((item) => item.properties["__line_uuid"] === editLineUuid);

        if (lineItem && this.editMode) {
            if (!lineItem) {
                throw new Error("Original cart line not found.");
            }
            const quantityValue = lineItem.quantity || 1;
            // Merge properties: preserve original outside edit range, override from combinedEntries
            const mergedProperties = { ...(lineItem.properties || {}) };
            // Always preserve original __line_uuid
            if (lineItem.properties?.__line_uuid) {
                mergedProperties["__line_uuid"] = String(lineItem.properties.__line_uuid);
            }
            Object.keys(combinedEntries).forEach((k) => {
                mergedProperties[k] = combinedEntries[k];
            });
            // Expose main character as a visible line item property
            if (combinedEntries["__main-character-name"]) {
                mergedProperties["für"] = combinedEntries["__main-character-name"];
            }
            const variantForSections = lineItem.id || lineItem.variant_id || this.variantId;
            await this.changeCartLine(lineItem.key, quantityValue, mergedProperties, variantForSections).then(() => {
                this.clearStorage();
            });

            // Update "für" property on child items (diary and dedication) if main character name changed
            if (combinedEntries["__main-character-name"]) {
                const parentLineUuid = mergedProperties["__line_uuid"];
                const updatedCart = await this.getCart();
                const childItems = updatedCart.items.filter(
                    (item) => item.properties["__parent_line_uuid"] === parentLineUuid
                );

                for (const childItem of childItems) {
                    const childProperties = { ...childItem.properties };
                    childProperties["für"] = combinedEntries["__main-character-name"];
                    await this.changeCartLine(childItem.key, childItem.quantity, childProperties, childItem.variant_id);
                }
            }

            await this.adjustCart();
            await this.checkForMissing().then(() => {
                window.location.href = window.routes.cart_page_url;
            });
            return;
        }

        if (!this.variantId) {
            throw new Error("Variant unavailable. Please select a valid variant.");
        }

        const parentProperties = { ...combinedEntries };
        // Expose main character as a visible line item property
        if (combinedEntries["__main-character-name"]) {
            parentProperties["für"] = combinedEntries["__main-character-name"];
        }

        const personalizationModal = document.querySelector("personalization-modal");
        const dedicationFeeVariantId = personalizationModal.getAttribute("data-dedication-variant-id");
        const diaryFeeVariantId = personalizationModal.getAttribute("data-diary-variant-id");

        parentProperties["__diary_variant_id"] = diaryFeeVariantId;
        parentProperties["__dedication_variant_id"] = dedicationFeeVariantId;
        parentProperties["__line_uuid"] = window.crypto.randomUUID();

        const items = [{ id: this.variantId, quantity: 1, properties: parentProperties }];

        const addDedication = String(combinedEntries["__add-dedication"]) === "true" && combinedEntries["__dedication"] !== "";
        const addDiary = String(combinedEntries["__add-diary"]) === "true";

        if (addDedication && dedicationFeeVariantId) {
            const dedicationProperties = {
                __parent_line_uuid: String(parentProperties["__line_uuid"]),
                __parent_variant_id: String(this.variantId),
                __variant_id: String(dedicationFeeVariantId),
            };
            // Add "für" property if main character name exists
            if (combinedEntries["__main-character-name"]) {
                dedicationProperties["für"] = combinedEntries["__main-character-name"];
            }
            items.push({
                id: dedicationFeeVariantId,
                quantity: 1,
                properties: dedicationProperties,
            });
        }
        if (addDiary && diaryFeeVariantId) {
            const diaryProperties = {
                __parent_line_uuid: String(parentProperties["__line_uuid"]),
                __parent_variant_id: String(this.variantId),
                __variant_id: String(diaryFeeVariantId),
            };
            // Add "für" property if main character name exists
            if (combinedEntries["__main-character-name"]) {
                diaryProperties["für"] = combinedEntries["__main-character-name"];
            }
            items.push({
                id: diaryFeeVariantId,
                quantity: 1,
                properties: diaryProperties,
            });
        }
        const response = await this.addToCart({ items, id: this.variantId });

        if (response) {
            console.log("PersonalizationModal: Added to cart");
            this.clearStorage();
            // await this.showUpsellModal();
            document.getElementById(`personalize-modal-${this.productId}`).hide();
            window.location.href = window.routes.cart_page_url;
            // Dispatch event for the parent line
            this.dispatchCartEvents({ id: this.variantId, quantity: 1, properties: parentProperties });
        }
    }

    /**
     * Instance method wrapper - delegates to static method
     */
    async adjustCart() {
        return PersonalizationModal.adjustCart();
    }

    /**
     * Instance method wrapper - delegates to static method
     */
    async checkForMissing() {
        return PersonalizationModal.checkForMissing();
    }

    // Utility to get the current modal root (supports append-to-body)
    getModalRoot() {
        const drawer = document.getElementById(`personalize-modal-${this.productId}`);
        return drawer || this;
    }

    // Step handling
    handleStepClick(event) {
        const step = event.currentTarget;
        const targetStepId = step.getAttribute("data-step-id");
        const currentStepId = this.getActiveStepId();
        if (parseInt(targetStepId, 10) > parseInt(currentStepId, 10)) {
            const validation = this.validateStep(currentStepId);
            this.updateErrors(currentStepId, validation.errors);
            if (!validation.valid) return;
        }
        this.clearErrors();
        this.activateStep(targetStepId);
    }

    handleNavClick(event) {
        event.preventDefault();
        const button = event.currentTarget;
        const stepId = button.getAttribute("data-next-step") || button.getAttribute("data-prev-step") || button.getAttribute("data-goto-step");
        if (!stepId) return;
        const currentStepId = this.getActiveStepId();
        const goingForward = parseInt(stepId, 10) > parseInt(currentStepId, 10);
        if (goingForward) {
            const validation = this.validateStep(currentStepId);
            this.updateErrors(currentStepId, validation.errors);
            if (!validation.valid) return;
        }
        this.clearErrors();
        this.displayCorrectForm(stepId);
        this.activateStep(stepId);
    }

    handleDocClick(event) {
        const root = this.getModalRoot();
        if (!root || !(event.target instanceof Element)) return;
        if (!root.contains(event.target)) return;
        const nextBtn = event.target.closest("[data-next-step]");
        const prevBtn = event.target.closest("[data-prev-step]");
        const gotoBtn = event.target.closest("[data-goto-step]");
        const stepBtn = nextBtn || prevBtn || gotoBtn;
        if (stepBtn) {
            event.preventDefault();
            const stepId = stepBtn.getAttribute("data-next-step") || stepBtn.getAttribute("data-prev-step") || stepBtn.getAttribute("data-goto-step");
            if (!stepId) return;
            const currentStepId = this.getActiveStepId();
            const goingForward = parseInt(stepId, 10) > parseInt(currentStepId, 10);
            if (goingForward) {
                const validation = this.validateStep(currentStepId);
                this.updateErrors(currentStepId, validation.errors);
                if (!validation.valid) return;
            }
            this.clearErrors();
            this.displayCorrectForm(stepId);
            this.activateStep(stepId);
        }
    }

    handleTriggerOpen() {
        // Don't clear storage when editing an existing item (openPersonalize mode)
        const params = new URLSearchParams(window.location.search || "");
        if (params.has("openPersonalize")) {
            return;
        }
        this.clearStorage();
    }

    clearStorage() {
        console.log("PersonalizationModal: Clearing storage");
        try {
            localStorage.removeItem(this.getStorageKey());
        } catch (e) {
            // noop
        }
    }

    displayCorrectForm(stepId) {
        const root = this.getModalRoot();
        const forms = root.querySelectorAll(".personalization-step-form");
        if (!forms || forms.length === 0) return;
        forms.forEach((form) => {
            const isActive = form.getAttribute("data-step-id") === String(stepId);
            form.classList.toggle("hidden", !isActive);
            if (isActive) {
                form.removeAttribute("hidden");
            } else {
                form.setAttribute("hidden", "");
            }
        });
    }

    activateStep(stepId) {
        if (!stepId) return;

        const root = this.getModalRoot();
        const headers = root.querySelectorAll(".personalization-step");
        headers.forEach((s) => s.classList.toggle("selected", s.getAttribute("data-step-id") === stepId));
        headers.forEach((s) => {
            if (s.getAttribute("data-step-id") == stepId) {
                s.setAttribute("aria-current", "step");
                s.classList.add("selected");
            } else {
                s.removeAttribute("aria-current");
                s.classList.remove("selected");
            }
        });
        const active = Array.from(headers).find((s) => s.classList.contains("selected"));
        if (active) {
            const title = active.getAttribute("data-title") || "";
            const description = active.getAttribute("data-description") || "";
            const dynTitle = root.querySelector("[data-step-dynamic-title]");
            const dynDesc = root.querySelector("[data-step-dynamic-description]");
            if (dynTitle) dynTitle.textContent = title;
            if (dynDesc) dynDesc.textContent = description;
        }
        this.showFormForStep(stepId);
    }

    showFormForStep(stepId) {
        const root = this.getModalRoot();
        const forms = root.querySelectorAll(".personalization-step-form");

        if (!forms || forms.length === 0) return;
        forms.forEach((form) => {
            const isActive = form.getAttribute("data-step-id") === String(stepId);
            if (isActive) {
                form.removeAttribute("hidden");
                form.querySelectorAll("input, textarea, select").forEach((input) => {
                    input.removeEventListener("input", this.boundHandleInput);
                    input.removeEventListener("focus", this.boundHandleFocus);
                    input.removeEventListener("pointerdown", this.boundHandlePointerDown);
                    input.removeEventListener("mousedown", this.boundHandlePointerDown);
                    input.removeEventListener("touchstart", this.boundHandlePointerDown);
                    input.addEventListener("input", this.boundHandleInput);
                    input.addEventListener("focus", this.boundHandleFocus);
                    input.addEventListener("pointerdown", this.boundHandlePointerDown);
                    input.addEventListener("mousedown", this.boundHandlePointerDown);
                    input.addEventListener("touchstart", this.boundHandlePointerDown, { passive: true });
                    const saved = this.getSavedFieldValue(input.name);
                    if (saved !== null && saved !== undefined && saved !== "") {
                        if (input.type === "radio" || input.type === "checkbox") {
                            if (input.value === saved) {
                                input.checked = true;
                            }
                        } else {
                            input.value = saved;
                            // Dispatch input event so overlay text updates
                            input.dispatchEvent(new Event("input", { bubbles: true }));
                        }
                    } else if (input.tagName === "TEXTAREA" && input.placeholder && !input.value) {
                        input.value = input.placeholder;
                        this.saveState({ ...(this.getSavedState() || {}), [input.name]: input.value });
                    }
                });
            } else {
                form.setAttribute("hidden", "");
            }
        });
    }

    handleFocus(event) {
        const target = event.target;
        if (!target || target.tagName !== "TEXTAREA") return;

        // If focus was triggered by a click/tap, keep the caret where the user clicked.
        // (Don't override selectionStart/End.)
        if (this.lastPointerDownTarget === target) {
            this.lastPointerDownTarget = null;
            return;
        }

        const placeCaretAtEnd = () => {
            try {
                const length = target.value ? target.value.length : 0;
                target.selectionStart = length;
                target.selectionEnd = length;
            } catch (e) {
                // noop
            }
        };
        // Defer to avoid browser auto-select-all on focus replacing our selection
        setTimeout(placeCaretAtEnd, 0);
    }

    handlePointerDown(event) {
        // Track pointer/mouse/touch initiated focus so we don't move the caret on focus.
        this.lastPointerDownTarget = event?.target || null;
    }

    // Persistence helpers
    getStorageKey() {
        return `personalization:${this.productId}`;
    }

    getSavedState() {
        try {
            const raw = localStorage.getItem(this.getStorageKey());
            return raw ? JSON.parse(raw) : {};
        } catch (e) {
            return {};
        }
    }

    saveState(next) {
        try {
            const current = this.getSavedState();
            localStorage.setItem(this.getStorageKey(), JSON.stringify({ ...current, ...next }));
        } catch (e) {
            // noop
        }
    }

    handleInput(event) {
        const target = event.target;
        if (!target?.name) return;
        this.saveState({ ...(this.getSavedState() || {}), [target.name]: target.value });
        const form = target.closest(".personalization-step-form");

        if (target.name === "__add-dedication") {
            const stepId = form?.getAttribute("data-step-id");
            const activeId = this.getActiveStepId();
            if (stepId && stepId === activeId && target.value === "false") {
                const validation = this.validateStep(stepId);
                this.updateErrors(stepId, validation.errors, /*silentOnValid*/ true);
            }
            return;
        }

        if (
            target.name === "__main-character-name" ||
            target.name === "__dedication"
        ) {
            return;
        }
        // Live-validate current step and update its error container only
        if (form) {
            const stepId = form.getAttribute("data-step-id");
            const activeId = this.getActiveStepId();
            if (stepId && stepId === activeId) {
                const validation = this.validateStep(stepId);
                this.updateErrors(stepId, validation.errors, /*silentOnValid*/ true);
            }
        }
    }

    getSavedFieldValue(name) {
        const state = this.getSavedState();
        return state ? state[name] : null;
    }

    async addToCart(cartData) {
        const hasMultiple = Array.isArray(cartData?.items);
        const payload = hasMultiple
            ? { items: cartData.items, sections: ["cart-bubble", "variant-added", "main-cart"] }
            : { ...cartData, sections: ["cart-bubble", "variant-added", "main-cart"] };
        const requestBody = JSON.stringify(payload);

        const response = await window.originalFetch(window.routes.cart_add_url, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
            body: requestBody,
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.description || errorData.message || "Failed to add to cart");
        }

        const result = await response.json();

        if (result.sections && result.sections["cart-bubble"]) {
            this.updateCartBubble(result.sections["cart-bubble"]);
        }

        return result;
    }

    /**
     * Instance method wrapper - delegates to static method
     */
    async getCart() {
        return PersonalizationModal.getCart();
    }

    async findExistingLineKey(variantId) {
        try {
            const cart = await this.getCart();
            if (!cart || !Array.isArray(cart.items)) return null;
            const variantIdNum = parseInt(String(variantId), 10);
            const item = cart.items.find((it) => it.id === variantIdNum || it.variant_id === variantIdNum);
            return item ? item.key : null;
        } catch (e) {
            return null;
        }
    }

    async changeCartLine(lineKey, quantity, properties, variantIdForSections) {
        const changeUrl = window.routes?.cart_change_url || `${window.Shopify?.routes?.root || "/"}cart/change.js`;
        const body = JSON.stringify({
            id: lineKey,
            quantity: quantity,
            properties: properties || {},
            sections: ["cart-bubble", "variant-added", "main-cart"],
        });

        const response = await window.originalFetch(changeUrl, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                "X-Requested-With": "XMLHttpRequest",
            },
            body,
        });

        if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.description || errorData.message || "Failed to update cart");
        }

        const result = await response.json();
        if (result.sections && result.sections["cart-bubble"]) {
            this.updateCartBubble(result.sections["cart-bubble"]);
        }
        return result;
    }

    getActiveStepId() {
        const root = this.getModalRoot();
        const active = root.querySelector(".personalization-step.selected");
        return active ? active.getAttribute("data-step-id") : "1";
    }

    validateStep(stepId) {
        const root = this.getModalRoot();
        const form = root.querySelector(`.personalization-step-form[data-step-id="${stepId}"]`);
        const errors = [];
        const sid = parseInt(stepId, 10);
        if (!form) return { valid: true, errors };

        if (sid === 1) {
            const input = form.querySelector("#main-character-name");
            const value = input ? String(input.value || "").trim() : "";
            if (!value) errors.push("Bitte gib den Namen der Hauptfigur ein.");
            if (value && value.length > 12) errors.push("Der Name der Hauptfigur darf maximal 12 Zeichen lang sein.");
            if (value && /[\p{L}]/u.test(value) && PERSONALIZATION_ALL_UPPERCASE_REGEX.test(value)) {
                errors.push("Bitte schreibe den Namen der Hauptfigur nicht komplett in Großbuchstaben.");
            }
        }

        if (sid === 2) {
            const radios = form.querySelectorAll('input[type="radio"][name="__add-dedication"]');
            const selected = Array.from(radios).find((r) => r.checked);
            if (!selected) {
                errors.push("Bitte wähle, ob du eine Widmung hinzufügen möchtest.");
            } else if (selected && selected.value === "true") {
                const dedication = form.querySelector('textarea[name="__dedication"]');
                const value = dedication ? String(dedication.value || "").trim() : "";
                if (value && PERSONALIZATION_DEDICATION_SIGNATURE_REGEX.test(value)) {
                    errors.push('Bitte passe den Text der persönlichen Widmung deinen Wünschen entsprechend an oder tausche zumindest "XXX" gegen deinen Namen aus.');
                }
            }
        }

        if (sid === 3) {
            const radios = form.querySelectorAll('input[type="radio"][name="__add-diary"]');
            const selected = Array.from(radios).some((r) => r.checked);
            if (!selected)  errors.push("Bitte wähle, ob du die Hardcover-Option auswählen möchtest.");
        }

        return { valid: errors.length === 0, errors };
    }

    updateErrors(stepId, errors, silentOnValid = false) {
        const root = this.getModalRoot();
        const container = root.querySelector(".personalization-steps-errors");
        if (!container) return;
        const errorSignature = JSON.stringify(errors || []);
        if (!errors || errors.length === 0) {
            if (!silentOnValid) container.innerHTML = "";
            container.classList.add("hidden");
            container.setAttribute("hidden", "");
            container.dataset.errorSignature = "";
            return;
        }
        const shouldAnimate =
            !silentOnValid &&
            !container.classList.contains("hidden") &&
            container.dataset.errorSignature === errorSignature;
        const list = errors.map((e) => `<li>${e}</li>`).join("");
        container.innerHTML = `<ul class="text-danger" role="alert">${list}</ul>`;
        container.classList.remove("hidden");
        container.removeAttribute("hidden");
        container.dataset.errorSignature = errorSignature;

        if (shouldAnimate) {
            container.classList.remove("personalization-steps-errors--shake");
            void container.offsetWidth;
            container.classList.add("personalization-steps-errors--shake");
        } else {
            container.classList.remove("personalization-steps-errors--shake");
        }
    }

    clearErrors() {
        const root = this.getModalRoot();
        const container = root.querySelector(".personalization-steps-errors");
        if (container) {
            container.innerHTML = "";
            container.classList.add("hidden");
            container.setAttribute("hidden", "");
        }
    }

    updateCartBubble(cartBubbleHtml) {
        PersonalizationModal.applyCartBubbleHtml(cartBubbleHtml);
    }

    static applyCartBubbleHtml(cartBubbleHtml) {
        const cartBubble = document.getElementById("CartBubble");
        if (!cartBubble) return;

        const parsedHtml = new DOMParser().parseFromString(cartBubbleHtml, "text/html");
        const renderedBubble = parsedHtml.querySelector("#CartBubble");
        const renderedCount = renderedBubble
            ? renderedBubble.textContent.trim()
            : parsedHtml.body.textContent.trim();

        if (renderedCount) {
            cartBubble.classList.remove("hidden");
            cartBubble.textContent = renderedCount;
        } else {
            cartBubble.classList.add("hidden");
            cartBubble.textContent = "";
        }
    }

    async showUpsellModal() {
        try {
            const activeId = this.getActiveStepId ? this.getActiveStepId() : "1";
            console.log("activeId", activeId);
            if (String(activeId) !== "4") {
                return;
            }
            const modal = document.getElementById("upsell-modal");
            if (modal) {
                modal.classList.remove("hidden");
            }
        } catch (e) {
            // noop
        }
    }

    async showDrawer() {
        if (window.addedToCartNotification === "drawer") {
            const cartModal = document.querySelector("cart-modal");
            if (cartModal && typeof cartModal.reloadContent === "function") {
                await cartModal.reloadContent();
                if (typeof cartModal.show === "function") {
                    cartModal.show();
                }
            }
        }
    }

    showErrorMessage(button, message) {
        // Find or create error message element
        let errorEl = button.parentNode.querySelector(".personalization-error");
        if (!errorEl) {
            errorEl = document.createElement("div");
            errorEl.className = "personalization-error text-danger text-sm mt-2";
            button.parentNode.appendChild(errorEl);
        }

        errorEl.textContent = message;
        errorEl.style.display = "block";

        // Hide error after 5 seconds
        setTimeout(() => {
            errorEl.style.display = "none";
        }, 5000);
    }

    setLoadingState(button, loading) {
        if (loading) {
            button.disabled = true;
            button.setAttribute("aria-loading", "true");
            button.style.position = "relative";

            if (!button.dataset.originalContent) {
                button.dataset.originalContent = button.innerHTML;
            }

            button.innerHTML = `
            <div style="display: flex; align-items: center; justify-content: center;">
              <svg 
                aria-hidden="true" 
                focusable="false" 
                class="spinner" 
                viewBox="0 0 66 66" 
                xmlns="http://www.w3.org/2000/svg"
                style="width: 16px; height: 16px; animation: spin 1s linear infinite;"
              >
                <circle class="path" fill="none" stroke="currentColor" stroke-width="6" cx="33" cy="33" r="30"></circle>
              </svg>
            </div>
            `;

            // Add spinner animation if not exists
            if (!document.getElementById("personalization-spinner-styles")) {
                const styles = document.createElement("style");
                styles.id = "personalization-spinner-styles";
                styles.textContent = `
                  @keyframes spin {
                    0% { transform: rotate(0deg); }
                    100% { transform: rotate(360deg); }
                  }
                `;
                document.head.appendChild(styles);
            }
        } else {
            button.disabled = false;
            button.removeAttribute("aria-loading");

            if (button.dataset.originalContent) {
                button.innerHTML = button.dataset.originalContent;
            }
        }
    }

    dispatchCartEvents(cartData) {
        document.dispatchEvent(
            new CustomEvent("product:added-to-cart", {
                detail: {
                    id: cartData.id,
                    quantity: cartData.quantity,
                    properties: cartData.properties,
                },
            })
        );
    }

    /**
     * Static method to set up global cart remove button listeners
     * Only runs once using a flag to prevent duplicate listeners
     */
    static setupGlobalRemoveButtonListeners() {
        // Check if already initialized
        if (PersonalizationModal._removeListenersInitialized) {
            return;
        }

        PersonalizationModal._removeListenersInitialized = true;

        // console.log("Setting up cart remove button listeners...");

        const removeButtons = document.querySelectorAll("[data-button-remove]");
        // console.log("Found remove buttons:", removeButtons.length, removeButtons);

        if (removeButtons.length === 0) {
            console.log("No remove buttons found on this page");
            return;
        }

        removeButtons.forEach((button, index) => {
            button.addEventListener("click", async function (event) {
                event.preventDefault();
                event.stopImmediatePropagation();

                try {
                    // Prevent double clicks
                    button.style.pointerEvents = 'none';
                    button.style.opacity = '0.5';

                    const cart = await PersonalizationModal.getCart();
                    if (!cart) {
                        window.location.reload();
                        return;
                    }

                    const lineIndex = parseInt(button.dataset.index, 10);
                    const itemToRemove = cart.items[lineIndex - 1]; 
                    const uuid = itemToRemove?.properties?.["__line_uuid"];
                    
                    const updates = {};
                    if (uuid) {
                        // Remove parent and all related children matching this UUID
                        cart.items.forEach(item => {
                            if (item.properties?.["__line_uuid"] === uuid || item.properties?.["__parent_line_uuid"] === uuid) {
                                updates[item.key] = 0;
                            }
                        });
                    } else if (itemToRemove) {
                        // Fallback if no UUID found but item exists
                        updates[itemToRemove.key] = 0;
                    } else {
                        window.location.reload();
                        return;
                    }

                    // Get closest section ID dynamically
                    const sectionElement = button.closest('.shopify-section');
                    const sectionId = sectionElement ? sectionElement.id.replace('shopify-section-', '') : 'main-cart';
                    const sectionsToUpdate = ["cart-bubble", sectionId];

                    const response = await window.originalFetch(`${window.Shopify?.routes?.root || "/"}cart/update.js`, {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ 
                            updates,
                            sections: sectionsToUpdate,
                            sections_url: window.location.pathname
                        }),
                    });

                    if (response.ok) {
                        const result = await response.json();
                        
                        // Update cart bubble
                        if (result.sections && result.sections["cart-bubble"]) {
                            PersonalizationModal.applyCartBubbleHtml(result.sections["cart-bubble"]);
                        }

                        // Update main cart (or drawer) structure
                        if (sectionElement && result.sections && result.sections[sectionId]) {
                            const parsedHtml = new DOMParser().parseFromString(result.sections[sectionId], "text/html");
                            
                            // Let's use either the inner wrapper or the full section
                            const newSection = parsedHtml.querySelector(`#${sectionElement.id}`) || 
                                               parsedHtml.querySelector('.shopify-section') || 
                                               parsedHtml.body;
                            
                            if (newSection) {
                                sectionElement.innerHTML = newSection.innerHTML;
                                
                                // Reset the flag so listeners can be attached to new elements inside the new HTML
                                PersonalizationModal._removeListenersInitialized = false;
                                PersonalizationModal.setupGlobalRemoveButtonListeners();
                            } else {
                                window.location.reload();
                            }
                        } else {
                            window.location.reload();
                        }
                    } else {
                        window.location.reload();
                    }
                } catch(error) {
                    console.error("Cart update error:", error);
                    window.location.reload();
                }
            }, true); // Use capture phase to intercept before cart-form listener
        });

        // console.log(`✓ Set up listeners on ${removeButtons.length} remove button(s)`);
    }

    /**
     * Static helper to get the current cart
     */
    static async getCart() {
        const response = await window.originalFetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
            method: "GET",
            headers: { Accept: "application/json" },
        });
        if (!response.ok) return null;
        return await response.json();
    }

    /**
     * Static method to adjust cart - removes orphaned items
     */
    static async adjustCart() {
        const cart = await PersonalizationModal.getCart();
        if (!cart) return;

        const parentLines = cart.items.filter((item) => !!item.properties["__line_uuid"]);

        // console.log("Adjusting cart - Parent lines:", JSON.stringify(parentLines));

        const itemsToRemove = [];

        parentLines.forEach((parentItem) => {
            const parentLineUuid = parentItem.properties["__line_uuid"];
            const addDiary = parentItem.properties["__add-diary"];
            const addDedication = parentItem.properties["__add-dedication"];
            const diaryVariantId = parentItem.properties["__diary_variant_id"];
            const dedicationVariantId = parentItem.properties["__dedication_variant_id"];

            // console.log(
            //     '25.02.2026',
            //     'add Diary',
            //     addDiary,
            //     'add Dedication',
            //     addDedication,
            //     'cart',
            //     JSON.stringify(cart.items)
            // );

            if (addDiary == "false" && diaryVariantId) {
                const diaryItem = cart.items.find(
                    (item) => item.properties["__parent_line_uuid"] == parentLineUuid && item.variant_id == diaryVariantId
                );

                if (diaryItem) {
                    console.log(`Found orphaned diary item for parent ${parentLineUuid}:`, diaryItem.product_title);
                    itemsToRemove.push({
                        key: diaryItem.key,
                        quantity: 0,
                        title: diaryItem.product_title,
                        reason: "diary not needed",
                    });
                }
            }

            if (addDedication == "false" && dedicationVariantId) {
                const dedicationItem = cart.items.find(
                    (item) => item.properties["__parent_line_uuid"] === parentLineUuid && item.variant_id == dedicationVariantId
                );

                if (dedicationItem) {
                    console.log(`Found orphaned dedication item for parent ${parentLineUuid}:`, dedicationItem.product_title);
                    itemsToRemove.push({
                        key: dedicationItem.key,
                        quantity: 0,
                        title: dedicationItem.product_title,
                        reason: "dedication not needed",
                    });
                }
            }
        });

        // Check for orphan items which have __parent_line_uuid that doesn't match any current parent items
        const validParentUuids = new Set(parentLines.map((item) => item.properties["__line_uuid"]));
        cart.items.forEach((item) => {
            const parentLineUuid = item.properties["__parent_line_uuid"];
            // If item has a parent reference but that parent doesn't exist in cart
            if (item && parentLineUuid && !validParentUuids.has(parentLineUuid)) {
                console.log(`Found orphaned item with invalid parent reference ${parentLineUuid}:`, item.product_title);
                itemsToRemove.push({
                    key: item.key,
                    quantity: 0,
                    title: item.product_title,
                    reason: `parent UUID ${parentLineUuid} not found in cart`,
                });
            }
        });

        // Remove all orphaned items
        if (itemsToRemove.length > 0) {
            console.log(`Removing ${itemsToRemove.length} orphaned item(s):`, JSON.stringify(itemsToRemove));

            const updates = (itemsToRemove || []).reduce((acc, item) => {
                const identifier = item?.key;
                if (identifier != null) {
                    acc[identifier] = 0;
                }
                return acc;
            }, {});
            // console.log('26.02.2026', 'updates', updates);  
            const removeResponse = await window.originalFetch(`${window.Shopify?.routes?.root || "/"}cart/update.js`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ updates }),
            });

            if (removeResponse.ok) {
                console.log(`✓ Removed ${itemsToRemove.length} orphaned item(s) in batch`);
                itemsToRemove.forEach((item) => {
                    console.log(`  - ${item.title} (${item.reason})`);
                });
            } else {
                console.error(`Failed to remove orphaned items:`, await removeResponse.text());
            }
        } else {
            console.log("No orphaned items to remove");
        }
    }

    /**
     * Static method to check for missing items and add them
     */
    static async checkForMissing() {
        const cart = await PersonalizationModal.getCart();
        if (!cart) return;

        const parentLines = cart.items.filter((item) => !!item.properties["__line_uuid"]);


        const itemsToAdd = [];

        parentLines.forEach((parent) => {
            const parentLineUuid = parent.properties["__line_uuid"];
            const addDiary = parent.properties["__add-diary"];
            const addDedication = parent.properties["__add-dedication"];
            const diaryVariantId = parent.properties["__diary_variant_id"];
            const dedicationVariantId = parent.properties["__dedication_variant_id"];

            // console.log(
            //     '25.02.2026',
            //     "add diary",
            //     addDiary,
            //     "add dedication",
            //     addDedication,
            // );

        if (addDiary == "true" && diaryVariantId) {
                const diaryExists = cart.items.some(
                    (item) => item.properties["__parent_line_uuid"] == parentLineUuid && item.variant_id == diaryVariantId
                );

                if (!diaryExists) {
                    console.log(`Missing diary item for parent ${parentLineUuid}, will add variant ${diaryVariantId}`);
                    const diaryProperties = {
                        __parent_line_uuid: parentLineUuid,
                        __parent_variant_id: String(parent.variant_id),
                        __variant_id: String(diaryVariantId),
                    };
                    // Add "für" property if main character name exists on parent
                    if (parent.properties["für"]) {
                        diaryProperties["für"] = parent.properties["für"];
                    }
                    itemsToAdd.push({
                        id: diaryVariantId,
                        quantity: 1,
                        properties: diaryProperties,
                    });
                }
            }

            if (addDedication == "true" && dedicationVariantId) {
                const dedicationExists = cart.items.some(
                    (item) => item.properties["__parent_line_uuid"] === parentLineUuid && item.variant_id == dedicationVariantId
                );

                if (!dedicationExists) {
                    console.log(`Missing dedication item for parent ${parentLineUuid}, will add variant ${dedicationVariantId}`);
                    const dedicationProperties = {
                        __parent_line_uuid: parentLineUuid,
                        __parent_variant_id: String(parent.variant_id),
                        __variant_id: String(dedicationVariantId),
                    };
                    // Add "für" property if main character name exists on parent
                    if (parent.properties["für"]) {
                        dedicationProperties["für"] = parent.properties["für"];
                    }
                    itemsToAdd.push({
                        id: dedicationVariantId,
                        quantity: 1,
                        properties: dedicationProperties,
                    });
                }
            }
        });

        if (itemsToAdd.length > 0) {
            console.log(`Adding ${itemsToAdd.length} missing item(s) to cart:`, itemsToAdd);

            const addResponse = await window.originalFetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({ items: itemsToAdd }),
            });

            if (addResponse.ok) {
                const result = await addResponse.json();
                console.log("✓ Successfully added missing items:", result);
            } else {
                console.error("Failed to add missing items");
            }
        } else {
            console.log("No missing items to add");
        }
    }
}


document.addEventListener("DOMContentLoaded", function () {
    const personalizationModals = document.querySelectorAll(".personalization-modal");
    personalizationModals.forEach((modal) => {
        if (!modal.hasAttribute("data-personalization-initialized")) {
            modal.setAttribute("data-personalization-initialized", "true");
        }
    });
});

// Auto-open and prefill from query params
document.addEventListener("DOMContentLoaded", async function () {
    const params = new URLSearchParams(window.location.search || "");
    if (!params.has("openPersonalize")) return;
    const modalEl = document.querySelector("personalization-modal");
    if (!modalEl) return;
    const productId = modalEl.getAttribute("data-product-id");
    if (!productId) return;

    const lineUuID = params.get("line_uuid");

    if (!lineUuID) {
        throw new Error("Line UUID is required to edit a book");
    }

    const cart = await fetch(`${window.Shopify?.routes?.root || "/"}cart.js`, {
        method: "GET",
        headers: { Accept: "application/json" },
    });
    if (!cart.ok) return;
    const cartData = await cart.json();

    const lineItem = cartData.items.find((item) => item.properties?.__line_uuid === lineUuID);
    if (!lineItem) return;

    const payload = {
        ...lineItem.properties,
    };

    const storageKey = `personalization:${productId}`;
    window.localStorage.setItem(storageKey, JSON.stringify(payload));

    const drawer = document.getElementById(`personalize-modal-${productId}`);
    if (drawer && typeof drawer.show === "function") {
        drawer.show();
    } else {
        const trigger = document.querySelector(`modal-trigger[target="#personalize-modal-${productId}"]`);
        if (trigger) trigger.click();
    }
    const modal = document.getElementById(`personalize-modal-${productId}`);
    if (modal) {
        Object.keys(payload).forEach((key) => {
            const textarea = modal.querySelector(`textarea[name="${key}"]`);
            if (textarea) textarea.value = payload[key];

            const select = modal.querySelector(`select[name="${key}"]`);
            if (select) select.value = payload[key];

            const input = modal.querySelector(`input[type="text"][name="${key}"]`);
            if (input) {
                input.value = payload[key];
                input.dispatchEvent(new Event("input", { bubbles: true }));
            }

            if (key === "__add-dedication" || key === "__add-diary") {
                const radio = modal.querySelector(`input[type="radio"][name="${key}"][value="${payload[key]}"]`);
                if (radio) radio.click();
            }
        });
    }
});

/**
 * Override global fetch to validate cart changes
 * Intercepts /cart/change requests and runs validation steps
 */
document.addEventListener("DOMContentLoaded", function () {
    // Store the original fetch function
    const originalFetch = window.fetch;
    window.originalFetch = originalFetch;

    // Override the global fetch method
    window.fetch = async function (...args) {
        const url = typeof args[0] === "string" ? args[0] : args[0]?.url || "";
        const isCartChangeRequest = url.includes("/cart/change");

        // If this is a cart change request, run validation after the request completes
        if (isCartChangeRequest) {
            try {
                // Execute the original fetch request
                const response = await originalFetch.apply(this, args);

                // If the request was successful, run validation
                if (response.ok) {
                    // Clone the response so we can read it and still return it
                    const clonedResponse = response.clone();

                    // Run validation asynchronously (don't block the response)
                    Promise.resolve()
                        .then(async () => {
                            try {
                                console.log("Running cart validation after /cart/change request...");
                                await PersonalizationModal.checkForMissing();
                                await PersonalizationModal.adjustCart();
                                console.log("Cart validation complete");
                                // window.location.reload();
                            } catch (error) {
                                console.error("Error during cart validation:", error);
                            }
                        })
                        .catch((error) => {
                            console.error("Error in validation promise chain:", error);
                        });

                    // Return the original response
                    return response;
                }

                // If request failed, return the response as-is
                return response;
            } catch (error) {
                console.error("Error in fetch override for /cart/change:", error);
                // Re-throw the error
                throw error;
            }
        }

        // For all other requests, use the original fetch
        return originalFetch.apply(this, args);
    };
});


// Register the custom element after the fetch override is set up
if (!customElements.get("personalization-modal")) {
    customElements.define("personalization-modal", PersonalizationModal);
}

/**
 * Initialize global cart remove button listeners
 * This runs independently of the personalization modal component
 */
document.addEventListener("DOMContentLoaded", function () {
    PersonalizationModal.setupGlobalRemoveButtonListeners();
});
